import { useEffect, useRef, useState } from "react";
import { Field, Fieldset } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ClockIcon, RefreshIcon } from "@heroicons/react/outline";
import FormInput from "../../Form/FormInput";
import FormRadioGroup from "../../Form/FormRadioGroup";
import FormError from "../../Form/FormError";
import Button from "../../Button";
import SlideOver from "../../Modal/SlideOver";
import { isBusinessWriteBlocked } from "../../../../Constants/business";
import WriteBlockGate from "../../Business/WriteBlockGate";
import {
  addLocalNotification,
  useAuthState,
  clearBusinessProduct,
  clearBusinessProductError,
  clearAdjustProductStock,
  requestAdjustProductStock,
  useBusinessProductDispatch,
  useBusinessProductState,
  useNotificationDispatch,
} from "../../../../Context";
import { cancelAPIRequest } from "../../../../Utils";

const STOCK_UPDATE_TYPES = [
  { label: "products.modal.stock.set", value: "SET", disabled: true },
  { label: "products.modal.stock.adjust", value: "ADJUST" },
];

const STOCK_ADJUSTMENT_TYPES = [
  { label: "products.modal.stock.add", value: "ADJUSTMENT_ADD" },
  { label: "products.modal.stock.subtract", value: "ADJUSTMENT_REMOVE" },
];

const ProductStockModal = ({ open, onClose, product, onLoadProduct }) => {
  const { t } = useTranslation();

  const [stockUpdateType, setStockUpdateType] = useState(STOCK_UPDATE_TYPES[1]);
  const [stockValue, setStockValue] = useState("");
  const [adjustmentType, setAdjustmentType] = useState(
    STOCK_ADJUSTMENT_TYPES[0],
  );
  const [description, setDescription] = useState("");
  const [errors, setErrors] = useState({});

  const dispatch = useBusinessProductDispatch();
  const notificationDispatch = useNotificationDispatch();
  const { user } = useAuthState();
  const writeBlocked = isBusinessWriteBlocked(user?.mainBusiness);

  const {
    request,
    businessProductLoading,
    adjustStockLoading,
    adjustStockError,
    adjustStockLedger,
  } = useBusinessProductState();

  // Clean up on unmount only. With `request` as a dependency this cleanup also
  // ran when a failed save reset `request` to null, wiping the error before it
  // was shown, and cancelled whatever request the page had in flight.
  const requestRef = useRef(request);
  requestRef.current = request;
  useEffect(() => {
    return () => {
      cancelAPIRequest(requestRef.current);
      clearBusinessProduct(dispatch);
      clearBusinessProductError(dispatch);
      clearAdjustProductStock(dispatch);
    };
  }, [dispatch]);

  // Handle update success
  useEffect(() => {
    if (
      product?.id &&
      adjustStockLedger &&
      adjustStockLedger?.productID === product?.id
    ) {
      // Clear form
      setStockValue("");
      setDescription("");
      setAdjustmentType(STOCK_ADJUSTMENT_TYPES[0]);

      // Clear ledger
      clearAdjustProductStock(dispatch);

      // Close modal so the notification can be visible
      onClose();

      // Show success notification
      addLocalNotification(notificationDispatch, {
        id: `stock-update-${product?.id}`,
        type: "success",
        title: "common.success",
        body: "products.notifications.stockUpdateSuccess",
        showFull: true,
      });
    }
  }, [notificationDispatch, product?.id, adjustStockLedger, dispatch, onClose]);

  const clearError = (field) =>
    setErrors((prev) => ({ ...prev, [field]: undefined }));

  const handleFieldChange = (setter, field) => (eventOrValue) => {
    const value =
      eventOrValue?.target !== undefined
        ? eventOrValue.target.value
        : eventOrValue;
    setter(value);
    clearError(field);
  };

  const validate = () => {
    const newErrors = {};

    if (!stockValue && stockUpdateType.value === "SET") {
      newErrors.stockValue = "products.errors.stockValueRequired";
    }

    if (!stockValue && stockUpdateType.value === "ADJUST") {
      newErrors.stockValue = "products.errors.adjustmentValueRequired";
    }

    if (stockValue && isNaN(Number(stockValue))) {
      newErrors.stockValue = "products.errors.stockValueNumber";
    }

    if (
      stockUpdateType.value === "ADJUST" &&
      adjustmentType.value === "ADJUSTMENT_REMOVE" &&
      Number(stockValue) > (product?.currentStock || 0)
    ) {
      newErrors.stockValue = "products.errors.cannotSubtract";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validate()) return;

    saveProductStock();
  };

  const saveProductStock = () => {
    const payload = {
      id: product.id,
      type: stockUpdateType.value === "SET" ? "SET" : adjustmentType.value,
      quantity: Number(stockValue),
      note: description,
    };

    requestAdjustProductStock(dispatch, payload);
  };

  const renderStockPreview = () => {
    // Don't show preview if user hasn't entered a value
    if (!stockValue) {
      return null;
    }

    const newStock =
      adjustmentType.value === "ADJUSTMENT_ADD"
        ? (product?.currentStock || 0) + Number(stockValue || 0)
        : (product?.currentStock || 0) - Number(stockValue || 0);

    const isNegative = newStock < 0;
    const bgColor = isNegative ? "bg-red-50" : "bg-blue-50";
    const textColor = isNegative ? "text-red-700" : "text-blue-700";

    return (
      <div className={`${bgColor} p-3 rounded-md`}>
        <div className={`text-sm ${textColor}`}>
          {t("products.modal.stock.previewLabel")}{" "}
          <span className="font-semibold">{newStock}</span>
        </div>
      </div>
    );
  };

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title="products.modal.title.setStock"
      footer={
        <div className="flex justify-end space-x-3">
          <Button type="button" variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            disabled={writeBlocked}
            loading={adjustStockLoading}
            type="submit"
            form="stock-form"
            variant="primary"
          >
            {t("common.save")}
          </Button>
        </div>
      }
    >
      <form id="stock-form" className="space-y-4 mb-6" onSubmit={handleSubmit}>
        <WriteBlockGate>
        <FormError error={adjustStockError} />
        {/* Fieldset is disabled during loading (businessProductLoading or adjustStockLoading) */}
        <Fieldset
          disabled={adjustStockLoading || businessProductLoading}
          className="space-y-4 data-[disabled]:opacity-50"
        >
          {/* Product Information */}
          <div className="bg-gray-50 p-4 rounded-md">
            <div>
              <h3 className="text-sm font-medium text-gray-500">
                {t("products.label_one")}
              </h3>
              <p className="mt-1 text-lg font-semibold text-gray-900">
                {product?.name}
              </p>
              <div className="mt-2 flex items-center">
                <span className="text-sm text-gray-500">
                  {t("products.modal.stock.label")}{" "}
                  <span className="text-sm text-gray-900">
                    {product?.currentStock || 0}
                  </span>
                </span>
                {Boolean(onLoadProduct) && (
                  <button
                    type="button"
                    onClick={onLoadProduct}
                    disabled={adjustStockLoading || businessProductLoading}
                    className="ml-2 text-blue-600 hover:text-blue-800 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {businessProductLoading ? (
                      <span className="animate-spin">
                        <RefreshIcon className="h-4 w-4" />
                      </span>
                    ) : (
                      <RefreshIcon className="h-4 w-4" />
                    )}
                  </button>
                )}
              </div>
              <div className="mt-3">
                <Link
                  to={`/v2/accounting/products/stock-history/${product?.id}`}
                  className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ClockIcon className="h-4 w-4 mr-1" />
                  {t("products.modal.stock.historyLabel")}
                </Link>
              </div>
            </div>
          </div>

          {/* Stock Update Type */}
          <FormRadioGroup
            label="products.fields.updateType"
            name="stockUpdateType"
            options={STOCK_UPDATE_TYPES}
            value={stockUpdateType}
            onChange={setStockUpdateType}
            gridClassName="grid-cols-2"
          />

          {/* Stock Value Input */}
          {stockUpdateType.value === "SET" && (
            <Field>
              <FormInput
                required
                label="products.fields.newStock"
                id="stock-value"
                name="stock_value"
                type="number"
                min="0"
                value={stockValue}
                onChange={handleFieldChange(setStockValue, "stockValue")}
                placeholder={t("products.placeholders.newStock")}
                error={errors.stockValue}
              />
            </Field>
          )}

          {/* Stock Adjustment */}
          {stockUpdateType.value === "ADJUST" && (
            <>
              <FormRadioGroup
                label="products.fields.adjustmentType"
                name="adjustmentType"
                options={STOCK_ADJUSTMENT_TYPES}
                value={adjustmentType}
                onChange={setAdjustmentType}
                gridClassName="grid-cols-2"
              />
              <Field>
                <FormInput
                  required
                  label="products.fields.adjustmentAmount"
                  id="adjustment-value"
                  name="adjustment_value"
                  type="number"
                  min="0"
                  value={stockValue}
                  onChange={handleFieldChange(setStockValue, "stockValue")}
                  placeholder={t("products.placeholders.adjustmentAmount")}
                  error={errors.stockValue}
                />
              </Field>
              <Field>
                <FormInput
                  label="products.fields.description"
                  id="adjustment-description"
                  name="adjustment_description"
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t("products.placeholders.adjustmentDescription")}
                />
              </Field>
              {renderStockPreview()}
            </>
          )}
        </Fieldset>
        </WriteBlockGate>
      </form>
    </SlideOver>
  );
};

export default ProductStockModal;
